import React, { useState, useEffect } from 'react';
import { ethers } from 'ethers';
import { useWallet } from '../utils/walletContext';
import { useClaims } from '../utils/claimContext';
import { switchToBaseMainnet } from '../utils/contractUtils';
import { isMetaMaskInstalled } from '../utils/metamaskDetector';
import ThemeToggle from './ThemeToggle';
import './Navbar.css';

function Navbar() {
  const {
    chainId,
    networkName,
    isConnected,
    connectWallet,
    disconnectWallet,
    formattedAccount,
    account
  } = useWallet();

  const { myClaimCount } = useClaims();

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isBaseNetwork, setIsBaseNetwork] = useState(false);
  const [hasMetaMask, setHasMetaMask] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [ensName, setEnsName] = useState(null);
  const [loadingEns, setLoadingEns] = useState(false);

  useEffect(() => {
    // Check if we are connected to Base network (ChainId 8453)
    if (chainId) {
      setIsBaseNetwork(chainId === 8453);
    }
  }, [chainId]);

  useEffect(() => {
    // Check if MetaMask is installed
    setHasMetaMask(isMetaMaskInstalled());
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const offset = window.scrollY;
      setScrolled(offset > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Resolve ENS name for the connected account
  useEffect(() => {
    const resolveEnsName = async () => {
      if (!account || !isConnected) {
        setEnsName(null);
        return;
      }

      try {
        setLoadingEns(true);
        // Use Ethereum mainnet provider for ENS resolution
        const mainnetProvider = new ethers.JsonRpcProvider('https://eth.llamarpc.com');
        const name = await mainnetProvider.lookupAddress(account);
        setEnsName(name);
      } catch (error) {
        console.log('ENS resolution failed:', error);
        setEnsName(null);
      } finally {
        setLoadingEns(false);
      }
    };

    resolveEnsName();
  }, [account, isConnected]);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleConnectWallet = async () => {
    try {
      await connectWallet();
    } catch (error) {
      console.error("Error connecting wallet:", error);
    }
  };

  const handleSwitchToBase = async () => {
    try {
      await switchToBaseMainnet();
    } catch (error) {
      console.error("Error switching to Base network:", error);
    }
  };

  return (
    <nav className={`
      fixed top-0 left-0 right-0 z-50 transition-all duration-300
      ${scrolled 
        ? 'bg-white/80 dark:bg-dark-900/80 backdrop-blur-md shadow-lg' 
        : 'bg-transparent'
      }
      border-b border-gray-200/20 dark:border-dark-700/30
    `}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-20">
          {/* Logo and Brand */}
          <div className="flex items-center">

            <a href="/" className="flex items-center group">
           <img src="evidencer-logo-2026-400w.png" alt="Unified Evidence Platform" 
           className="w-20 h-16 sm:w-20 sm:h-16 md:w-20 md:h-16 lg:w-20 lg:h-16 transition-transform group-hover:scale-105" />
            </a>
&nbsp; &nbsp;

              <span className="font-bold text-xl bg-gradient-to-r from-primary-600 to-primary-800 bg-clip-text text-transparent">
                Unified Evidencer
              </span>

          </div>

          {/* Mobile menu button */}
          <div className="flex items-center md:hidden space-x-2">
            <ThemeToggle />
            <button
              type="button"
              className="inline-flex items-center justify-center p-2 rounded-md text-gray-400 dark:text-gray-300 hover:text-gray-500 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-dark-800 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500"
              onClick={toggleMobileMenu}
            >
              <span className="sr-only">Open main menu</span>
              <div className={`${mobileMenuOpen ? 'hidden' : 'block'} h-6 w-6`}>
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </div>
              <div className={`${mobileMenuOpen ? 'block' : 'hidden'} h-6 w-6`}>
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
            </button>
          </div>

          {/* Desktop navigation */}
          <div className="hidden md:flex md:items-center md:space-x-1">
            <a href="/about" className="px-3 py-2 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-gray-50 dark:hover:bg-dark-800 transition-all duration-200 relative group">
              About
              <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-primary-600 to-primary-800 transition-all duration-300 group-hover:w-full"></span>
            </a>
            <a href="/file-claim" className="px-4 py-2 rounded-lg text-sm font-medium bg-primary-600 text-white hover:bg-primary-700 transition-all duration-200 shadow-md hover:shadow-lg">
              File a Claim
            </a>
            <a href="/register-case" className="px-3 py-2 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-gray-50 dark:hover:bg-dark-800 transition-all duration-200 relative group">
              Registry
              <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-primary-600 to-primary-800 transition-all duration-300 group-hover:w-full"></span>
            </a>
            <a href="/agreements" className="px-3 py-2 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-gray-50 dark:hover:bg-dark-800 transition-all duration-200 relative group">
              Agreements
              <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-primary-600 to-primary-800 transition-all duration-300 group-hover:w-full"></span>
            </a>
            <a href="/view-cases" className="px-3 py-2 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-gray-50 dark:hover:bg-dark-800 transition-all duration-200 relative group">
              My Claims
              {isConnected && myClaimCount > 0 && (
                <span className="absolute -top-1.5 -right-1.5 bg-primary-600 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                  {myClaimCount}
                </span>
              )}
              <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-primary-600 to-primary-800 transition-all duration-300 group-hover:w-full"></span>
            </a>
            <a href="/how-to" className="px-3 py-2 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-gray-50 dark:hover:bg-dark-800 transition-all duration-200 relative group">
              Guide
              <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-primary-600 to-primary-800 transition-all duration-300 group-hover:w-full"></span>
            </a>
            
            {/* Theme Toggle */}
            <ThemeToggle className="mx-2" />
            
            {/* Wallet Connection */}
            <div className="ml-2 flex items-center space-x-2">
              {isConnected ? (
                <div className="flex items-center space-x-2">
                  <div className={`px-2 py-1 rounded-md text-xs font-medium ${isBaseNetwork ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200' : 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200'}`}>
                    {networkName}
                  </div>
                  <div className="px-3 py-1 rounded-md text-sm font-medium bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200">
                    {ensName ? (
                      <span className="flex items-center gap-1">
                        <span className="font-semibold">{ensName}</span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">({formattedAccount})</span>
                      </span>
                    ) : (
                      formattedAccount
                    )}
                  </div>
                  {!isBaseNetwork && (
                    <button
                      onClick={handleSwitchToBase}
                      className="px-3 py-1 rounded-md text-xs font-medium bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 hover:bg-blue-200 dark:hover:bg-blue-800"
                    >
                      Switch to Base
                    </button>
                  )}
                  <button
                    onClick={disconnectWallet}
                    className="px-3 py-1 rounded-md text-sm font-medium text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/30"
                  >
                    Disconnect
                  </button>
                </div>
              ) : (
                hasMetaMask ? (
                  <button
                    onClick={handleConnectWallet}
                    className="px-3 py-2 rounded-md text-sm font-medium bg-blue-600 text-white hover:bg-blue-700"
                  >
                    Connect Wallet
                  </button>
                ) : (
                  <a 
                    href="https://metamask.io/download/" 
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-3 py-2 rounded-md text-sm font-medium bg-orange-500 text-white hover:bg-orange-600 flex items-center"
                  >
                    <span className="mr-1">🦊</span>
                    Install MetaMask
                  </a>
                )
              )}
            </div>
          </div>

        </div>
      </div>

      {/* Mobile menu */}
      <div className={`${mobileMenuOpen ? 'block' : 'hidden'} md:hidden`}>
        <div className="bg-white/95 dark:bg-dark-900/95 backdrop-blur-md border border-gray-200/50 dark:border-gray-700/50 mx-4 my-2 rounded-2xl shadow-lg animate-fade-in">
          <div className="p-4 space-y-3">
            <a href="/" className="block px-4 py-3 text-base font-medium text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-white dark:hover:bg-opacity-10 rounded-xl transition-all">
              Home
            </a>
            <a href="/file-claim" className="block px-4 py-3 text-base font-medium text-white bg-gradient-to-r from-primary-600 to-primary-500 rounded-xl hover:from-primary-700 hover:to-primary-600 transition-all">
              📝 File a Claim
            </a>
            <a href="/view-cases" className="block px-4 py-3 text-base font-medium text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-white dark:hover:bg-opacity-10 rounded-xl transition-all relative">
              My Claims
              {isConnected && myClaimCount > 0 && (
                <span className="ml-2 inline-block bg-primary-600 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                  {myClaimCount}
                </span>
              )}
            </a>
            <a href="/register-case" className="block px-4 py-3 text-base font-medium text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-white dark:hover:bg-opacity-10 rounded-xl transition-all">
              🔐 Registry
            </a>
            <a href="/agreements" className="block px-4 py-3 text-base font-medium text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-white dark:hover:bg-opacity-10 rounded-xl transition-all">
              📄 Agreements
            </a>
            <a href="/about" className="block px-4 py-3 text-base font-medium text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-white dark:hover:bg-opacity-10 rounded-xl transition-all">
              About
            </a>
            <a href="/how-to" className="block px-4 py-3 text-base font-medium text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-white dark:hover:bg-opacity-10 rounded-xl transition-all">
              📘 Guide
            </a>
            
            {/* Mobile Wallet Connection */}
            <div className="mt-4 px-3">
            {isConnected ? (
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <div className={`px-2 py-1 rounded-md text-xs font-medium ${isBaseNetwork ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200' : 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200'}`}>
                    {networkName}
                  </div>
                  <div className="px-3 py-1 rounded-md text-sm font-medium bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200">
                    {ensName ? (
                      <span className="flex flex-col">
                        <span className="font-semibold">{ensName}</span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">{formattedAccount}</span>
                      </span>
                    ) : (
                      formattedAccount
                    )}
                  </div>
                </div>
                {!isBaseNetwork && (
                  <button
                    onClick={handleSwitchToBase}
                    className="w-full px-3 py-2 rounded-md text-sm font-medium bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 hover:bg-blue-200 dark:hover:bg-blue-800"
                  >
                    Switch to Base Network
                  </button>
                )}
                <button
                  onClick={disconnectWallet}
                  className="w-full px-3 py-2 rounded-md text-sm font-medium text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800 hover:bg-red-50 dark:hover:bg-red-900/30"
                >
                  Disconnect Wallet
                </button>
              </div>
            ) : (
              hasMetaMask ? (
                <button
                  onClick={handleConnectWallet}
                  className="w-full px-3 py-2 rounded-md text-sm font-medium bg-blue-600 text-white hover:bg-blue-700"
                >
                  Connect Wallet
                </button>
              ) : (
                <a 
                  href="https://metamask.io/download/" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full px-3 py-2 rounded-md text-sm font-medium bg-orange-500 text-white hover:bg-orange-600 flex items-center justify-center"
                >
                  <span className="mr-1">🦊</span>
                  Install MetaMask
                </a>
              )
            )}
            </div>
          </div>


        </div>
      </div>
    </nav>
  );
}

export default Navbar;